import React, { FC, useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";

import {
  getOutreachAuthUrl,
  getHubSpotAuthUrl,
  onAuthStateChangedHook,
  subToDbUser,
  sendVerificationEmail,
  completePendingOrgJoin,
} from "../../services/firebase";
import { Crm, useCrm } from "../../components/CrmUi/useCrm";
import { readSignupCrm } from "../../components/CrmUi/authCrm";
import { AuthButton, AuthError, AuthPanel, AuthShell } from "./AuthUi";

/**
 * Signup matched an organization that already exists (an invite or a
 * colleague on the same email domain). Joining it waits until the user proves
 * the email is theirs, so this asks them to click the verification link.
 */
const VerifyEmailToJoin: FC<{ email: string; crm: Crm }> = ({ email, crm }) => {
  const [isChecking, setIsChecking] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const sentOnce = useRef(false);

  const send = async () => {
    try {
      setIsSending(true);
      setError("");
      await sendVerificationEmail();
      setMessage(`We sent a verification link to ${email}.`);
    } catch (err) {
      console.error("Failed to send verification email:", err);
      setError("We could not send the email. Wait a minute and try again.");
    } finally {
      setIsSending(false);
    }
  };

  useEffect(() => {
    if (sentOnce.current) return;
    sentOnce.current = true;
    const key = `verificationEmailSent:${email}`;
    try {
      if (sessionStorage.getItem(key)) {
        setMessage(`We sent a verification link to ${email}.`);
        return;
      }
      sessionStorage.setItem(key, "1");
    } catch {
      // Storage can be blocked (private windows): the link is simply sent again.
    }
    send();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  const check = async () => {
    try {
      setIsChecking(true);
      setError("");
      if ((await completePendingOrgJoin()) === "unverified") {
        setError("Your email is not verified yet. Click the link in the email, then try again.");
        return;
      }
      window.location.href = "/app/dashboard";
    } catch (err) {
      console.error("Failed to join organization:", err);
      setError("Something went wrong joining your team. Please try again.");
    } finally {
      setIsChecking(false);
    }
  };

  return (
    <AuthPanel
      crm={crm}
      title="Verify your email to join your team"
      intro={`Your company already uses boring. Click the link we emailed to ${email}, then come back here.`}
    >
      <AuthButton onClick={check} disabled={isSending} loading={isChecking} busyLabel="Checking">
        I verified my email
      </AuthButton>
      <AuthButton secondary onClick={send} disabled={isChecking} loading={isSending} busyLabel="Sending">
        Send the email again
      </AuthButton>
      {message && (
        <p className="ba-note" role="status">
          {message}
        </p>
      )}
      <AuthError>{error}</AuthError>
    </AuthPanel>
  );
};

/**
 * After sign up: connect the CRM. The one they signed up for (authCrm.ts's
 * boring.signupCrm, else the dashboard's) leads, in that CRM's look; the
 * other stays one click away.
 */
const SelectIntegration: FC = () => {
  const orgCrm = useCrm();
  const crm: Crm = readSignupCrm() ?? orgCrm;
  const [isProcessing, setIsProcessing] = useState<"outreach" | "hubspot" | null>(null);
  const [error, setError] = useState("");
  // The signup trigger sets pendingOrgJoin a moment after the account is
  // created, so follow the user document instead of reading it once.
  const [pendingJoinEmail, setPendingJoinEmail] = useState<string | null>(null);

  useEffect(() => {
    let unsubscribeUser: (() => void) | undefined;
    const unsubscribeAuth = onAuthStateChangedHook((authUser) => {
      unsubscribeUser?.();
      unsubscribeUser = authUser
        ? subToDbUser(authUser.uid, (dbUser) => {
            setPendingJoinEmail(dbUser?.pendingOrgJoin ? authUser.email || "" : null);
          })
        : undefined;
    });
    return () => {
      unsubscribeAuth();
      unsubscribeUser?.();
    };
  }, []);

  const handleOutreachConnect = async () => {
    try {
      setIsProcessing("outreach");
      setError("");

      const { result, status } = await getOutreachAuthUrl();
      if (status === 200 && result?.outreachAuthUrl) {
        window.location.href = result.outreachAuthUrl;
      } else {
        setIsProcessing(null);
        setError("Failed to get Outreach OAuth URL. Please try again.");
      }
    } catch (error) {
      console.error("Failed to get Outreach auth URL:", error);
      setIsProcessing(null);
      setError("Failed to connect to Outreach. Please try again.");
    }
  };

  const handleHubSpotConnect = async () => {
    try {
      setIsProcessing("hubspot");
      setError("");

      const { result, status } = await getHubSpotAuthUrl();
      if (status === 200 && result?.hubspotAuthUrl) {
        window.location.href = result.hubspotAuthUrl;
      } else {
        setIsProcessing(null);
        setError("Failed to get HubSpot OAuth URL. Please try again.");
      }
    } catch (error) {
      console.error("Failed to get HubSpot auth URL:", error);
      setIsProcessing(null);
      setError("Failed to connect to HubSpot. Please try again.");
    }
  };

  const connect = { outreach: handleOutreachConnect, hubspot: handleHubSpotConnect };
  const name = { outreach: "Outreach", hubspot: "HubSpot" };
  const other: Crm = crm === "hubspot" ? "outreach" : "hubspot";

  return (
    <AuthShell crm={crm}>
      <Helmet>
        <title>Connect {name[crm]} | boring</title>
      </Helmet>
      {pendingJoinEmail !== null ? (
        <VerifyEmailToJoin email={pendingJoinEmail} crm={crm} />
      ) : (
        <AuthPanel
          crm={crm}
          title={`Connect ${name[crm]}`}
          intro={`boring works inside your ${name[crm]}. Connect your account to get started.`}
        >
          <AuthButton
            onClick={connect[crm]}
            disabled={isProcessing !== null}
            loading={isProcessing === crm}
            busyLabel={`Connecting to ${name[crm]}`}
          >
            Connect to {name[crm]}
          </AuthButton>
          <AuthButton
            secondary
            onClick={connect[other]}
            disabled={isProcessing !== null}
            loading={isProcessing === other}
            busyLabel={`Connecting to ${name[other]}`}
          >
            Use {name[other]} instead
          </AuthButton>
          <AuthError>{error}</AuthError>
        </AuthPanel>
      )}
    </AuthShell>
  );
};

export default SelectIntegration;
